import { assign, createMachine, raise } from 'xstate';
import { pure } from 'xstate/lib/actions';
import { paymentTypes } from '../const/paymentOptions';
import { getAutopayEligibilityDetail, isPayLaterProduct, isSecuredInstallmentLoanProduct } from '../helpers';
import { isManageAutopayFlow, isModifyIneligiblePriorToFirstStatement } from '../helpers/autopayHelpers';
const ENROLLMENT_INITIALIZING = 'enrollment.initializing';
const ACCORDION_INITIALIZING = 'accordion.initializing';
const AutopayBanner = createMachine({
    predictableActionArguments: true,
    preserveActionOrder: true,
    id: 'autopayBanner',
    schema: {
        events: {}
    },
    type: 'parallel',
    states: {
        accordion: {
            initial: 'initializing',
            states: {
                initializing: {
                    always: [
                        { target: 'disabled', cond: 'shouldExpandButtonBeDisabled' },
                        {
                            target: 'enabled'
                        }
                    ]
                },
                enabled: {
                    initial: 'expanded',
                    states: {
                        collapsed: {
                            on: {
                                EXPAND: {
                                    target: 'expanded'
                                }
                            }
                        },
                        expanded: {
                            on: {
                                COLLAPSE: {
                                    target: 'collapsed'
                                }
                            }
                        }
                    }
                },
                disabled: {}
            }
        },
        enrollment: {
            initial: 'initializing',
            states: {
                initializing: {
                    always: [
                        {
                            target: 'unenrolled',
                            cond: 'isUnenrolled'
                        },
                        {
                            target: 'enrolled',
                            cond: 'isEnrolled'
                        }
                    ]
                },
                unenrolled: {
                    initial: 'idle',
                    states: {
                        idle: {
                        // Showing "Enroll in Autopay" button
                        },
                        enrolling: {
                        // Showing "Enroll in autopay and steer clear of missed payments!" message
                        }
                    }
                },
                enrolled: {
                    initial: 'initializing',
                    states: {
                        initializing: {
                            always: [
                                {
                                    target: 'idle',
                                    cond: 'isNotManagingAutopay'
                                },
                                {
                                    target: 'managing'
                                }
                            ]
                        },
                        idle: {
                        // Showing "Manage Autopay" button
                        },
                        managing: {
                            // Showing "Turn Autopay Off" button
                            on: {
                                PROMPT_UNENROLL_CONFIRMATION: 'unenrolling'
                            }
                        },
                        unenrolling: {
                            // Showing "YES, TURN AUTOPAY OFF" button
                            on: {
                                DECLINE_UNENROLL: 'managing'
                            }
                        }
                    }
                }
            }
        },
        visibility: {
            initial: 'visible',
            states: {
                visible: {
                    on: {
                        HIDE_BANNER: { target: 'hidden' }
                    }
                },
                hidden: {
                    on: {
                        SHOW_BANNER: { target: 'visible' }
                    }
                }
            }
        }
    },
    on: {
        UPDATE_BANNER_VISIBILITY: {
            actions: 'handleAutopayBannerVisibility'
        },
        INCREMENT_CONVERSION: {
            target: [ENROLLMENT_INITIALIZING, ACCORDION_INITIALIZING]
        },
        SET_AUTOPAY_FLOW: {
            target: [ENROLLMENT_INITIALIZING, ACCORDION_INITIALIZING],
            actions: assign({
                currentPaymentFlow: (_, { mainMachineContext }) => {
                    return mainMachineContext.currentPaymentFlow;
                }
            })
        },
        SET_ONE_TIME_PAYMENT_FLOW: {
            target: [ENROLLMENT_INITIALIZING, ACCORDION_INITIALIZING],
            actions: assign({
                currentPaymentFlow: (_, { mainMachineContext }) => {
                    return mainMachineContext.currentPaymentFlow;
                }
            })
        },
        SET_PAYOFF_PAYMENT_FLOW: {
            target: [ENROLLMENT_INITIALIZING, ACCORDION_INITIALIZING],
            actions: assign({
                currentPaymentFlow: (_, { mainMachineContext }) => {
                    return mainMachineContext.currentPaymentFlow;
                }
            })
        }
    }
}, {
    guards: {
        isEnrolled: ({ apiMachineRef }) => {
            const { data, isError } = apiMachineRef.getSnapshot().context.autopayStatusQueryResult;
            if (isError) {
                return false;
            }
            return data.enrollment === true;
        },
        isEnrollmentEligible: ({ apiMachineRef }) => {
            const { data: paymentEnterpriseEligibility } = apiMachineRef.getSnapshot().context
                .paymentEnterpriseEligibilityQueryResult;
            return getAutopayEligibilityDetail(paymentEnterpriseEligibility, 'AUTOPAY_ENROLL').eligible;
        },
        isPriorToFirstStatement: ({ apiMachineRef }) => {
            const { data: paymentEnterpriseEligibility } = apiMachineRef.getSnapshot().context
                .paymentEnterpriseEligibilityQueryResult;
            return isModifyIneligiblePriorToFirstStatement(paymentEnterpriseEligibility);
        },
        isModifyEligible: ({ apiMachineRef }) => {
            const { data: paymentEnterpriseEligibility } = apiMachineRef.getSnapshot().context
                .paymentEnterpriseEligibilityQueryResult;
            return getAutopayEligibilityDetail(paymentEnterpriseEligibility, 'AUTOPAY_MODIFY').eligible;
        },
        isNotManagingAutopay: ({ currentPaymentFlow }) => {
            const paymentFlowActorRef = currentPaymentFlow[1];
            return !isManageAutopayFlow(paymentFlowActorRef.getSnapshot().value);
        },
        isUnenrolled: ({ apiMachineRef }) => {
            const { data: autopayStatus } = apiMachineRef.getSnapshot().context.autopayStatusQueryResult;
            return autopayStatus.enrollment === false;
        },
        shouldExpandButtonBeDisabled: ({ apiMachineRef, currentPaymentFlow, featureFlags }) => {
            const [paymentType, paymentFlowActorRef] = currentPaymentFlow;
            const { accountSummaryQueryResult: { data: { product: { type: productType } } }, autopayStatusQueryResult: { data: autopayStatus }, paymentEnterpriseEligibilityQueryResult: { data: paymentEnterpriseEligibility } } = apiMachineRef.getSnapshot().context;
            const isUnenrolled = autopayStatus.enrollment === false;
            const isEnrollmentEligible = getAutopayEligibilityDetail(paymentEnterpriseEligibility, 'AUTOPAY_ENROLL').eligible;
            const isAutopayModifyEligible = getAutopayEligibilityDetail(paymentEnterpriseEligibility, 'AUTOPAY_MODIFY').eligible;
            const isAutopayUnEnrollInEligible = isPayLaterProduct(productType) &&
                !getAutopayEligibilityDetail(paymentEnterpriseEligibility, 'AUTOPAY_UNENROLL').eligible;
            // Secured Installment Autopay is based on feature flag
            if (isSecuredInstallmentLoanProduct(productType) &&
                !featureFlags.isSecuredInstallmentAutopayEnabled) {
                return true;
            }
            if (
            // isUnenrolled And EnrollmentNotEligible
            isUnenrolled &&
                !isEnrollmentEligible) {
                return true;
            }
            if (isManageAutopayFlow(paymentFlowActorRef.getSnapshot().value)) {
                // Pay Later Product Managing Autopay
                if (isPayLaterProduct(productType) && isAutopayUnEnrollInEligible) {
                    return true;
                }
                // Products other than Pay Later Managing Autopay
                if (!isPayLaterProduct(productType) && !isAutopayModifyEligible) {
                    return true;
                }
            }
            if (paymentType === 'autopay') {
                const autopayContext = paymentFlowActorRef.getSnapshot()
                    .context;
                if (autopayContext.successType === 'unenrolling') {
                    return true;
                }
            }
            return false;
        }
    },
    actions: {
        handleAutopayBannerVisibility: pure(({ currentPaymentFlow, apiMachineRef, additionalProps }) => {
            var _a, _b;
            const [paymentType, paymentTypeActorRef] = currentPaymentFlow;
            const isAutopayEnrolled = (_a = apiMachineRef.getSnapshot().context.autopayStatusQueryResult.data) === null || _a === void 0 ? void 0 : _a.enrollment;
            const isMultiPaymentFlow = ((_b = additionalProps === null || additionalProps === void 0 ? void 0 : additionalProps.flow) === null || _b === void 0 ? void 0 : _b.type) === 'MULTI_PAYMENT_FLOW';
            const currentFlowPage = paymentTypeActorRef.getSnapshot().value;
            const REVIEW_PAGE = currentFlowPage === 'review';
            const SUBMITTING_PAGE = currentFlowPage === 'submitting';
            const SUCCESS_PAGE = currentFlowPage === 'success';
            const EDIT_PAGE = currentFlowPage === 'edit';
            if (isMultiPaymentFlow) {
                return raise('HIDE_BANNER');
            }
            if (paymentType === paymentTypes.ONCE &&
                (REVIEW_PAGE || SUBMITTING_PAGE)) {
                return raise('HIDE_BANNER');
            }
            if (paymentType === paymentTypes.PAYOFF &&
                (REVIEW_PAGE || SUBMITTING_PAGE || SUCCESS_PAGE)) {
                return raise('HIDE_BANNER');
            }
            if (paymentType === paymentTypes.AUTOPAY &&
                EDIT_PAGE &&
                !isAutopayEnrolled) {
                return raise('HIDE_BANNER');
            }
            return raise('SHOW_BANNER');
        })
    }
});
export default AutopayBanner;
